import React, { memo, useCallback, useRef, useState } from "react";
import {
    X,
    Upload,
    Download,
    Trash2,
    FileText,
    Image,
    File,
    Eye,
    XCircle,
    CheckCircle,
    AlertCircle,
    AlertTriangle
} from "lucide-react";
import dayjs from "dayjs";

const DocumentsModal = memo(({
    isOpen,
    onClose,
    selectedBC,
    documents,
    uploadLoading,
    onUpload,
    onDownload,
    onDelete,
    onPreview,
}) => {
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState(null);
    const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
    const [deleteConfirmation, setDeleteConfirmation] = useState({
        isOpen: false,
        docId: null,
        fileName: ''
    });

    // Reset states when modal closes
    const handleClose = useCallback(() => {
        setSelectedFile(null);
        setUploadProgress(0);
        setUploadError(null);
        setUploadStatus('idle');
        setDeleteConfirmation({ isOpen: false, docId: null, fileName: '' });
        onClose();
    }, [onClose]);

    const handleFileSelect = useCallback((event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file size (10MB max)
            if (file.size > 10 * 1024 * 1024) {
                setUploadError("Le fichier ne doit pas dépasser 10MB");
                setUploadStatus('error');
                return;
            }

            // Validate file type
            const allowedTypes = [
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'image/jpeg',
                'image/png'
            ];

            if (!allowedTypes.includes(file.type) && !file.name.match(/\.(pdf|doc|docx|xls|xlsx|jpg|jpeg|png)$/i)) {
                setUploadError("Type de fichier non autorisé");
                setUploadStatus('error');
                return;
            }

            setSelectedFile(file);
            setUploadError(null);
            setUploadStatus('idle');
        }
    }, []);

    const handleUpload = useCallback(async () => {
        if (!selectedFile || !selectedBC) return;

        setUploadStatus('uploading');
        setUploadProgress(0);
        setUploadError(null);

        try {
            // Simulate progress for better UX
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return 90;
                    }
                    return prev + 10;
                });
            }, 200);

            const result = await onUpload(selectedBC.idBC, selectedFile);

            clearInterval(progressInterval);

            if (result.success) {
                setUploadProgress(100);
                setUploadStatus('success');

                // Reset after success
                setTimeout(() => {
                    setSelectedFile(null);
                    setUploadProgress(0);
                    setUploadStatus('idle');
                }, 1500);

                // Reset input
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            } else {
                setUploadError(result.error || "Erreur lors de l'upload");
                setUploadStatus('error');
            }
        } catch (error) {
            setUploadError("Erreur lors de l'upload");
            setUploadStatus('error');
        }
    }, [selectedFile, selectedBC, onUpload]);

    const handleCancelUpload = useCallback(() => {
        setSelectedFile(null);
        setUploadProgress(0);
        setUploadError(null);
        setUploadStatus('idle');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    const handleTriggerFileInput = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleDeleteClick = useCallback((docId, fileName) => {
        setDeleteConfirmation({
            isOpen: true,
            docId,
            fileName
        });
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (deleteConfirmation.docId && selectedBC) {
            await onDelete(deleteConfirmation.docId, selectedBC.idBC);
            setDeleteConfirmation({ isOpen: false, docId: null, fileName: '' });
        }
    }, [deleteConfirmation.docId, selectedBC, onDelete]);

    const handleCancelDelete = useCallback(() => {
        setDeleteConfirmation({ isOpen: false, docId: null, fileName: '' });
    }, []);

    const getFileIcon = useCallback((filename) => {
        const ext = filename.split('.').pop()?.toLowerCase();
        if (['pdf'].includes(ext)) return <FileText className="w-5 h-5 text-red-500" />;
        if (['doc', 'docx'].includes(ext)) return <FileText className="w-5 h-5 text-blue-500" />;
        if (['xls', 'xlsx'].includes(ext)) return <FileText className="w-5 h-5 text-green-500" />;
        if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return <Image className="w-5 h-5 text-purple-500" />;
        return <File className="w-5 h-5 text-gray-500" />;
    }, []);

    const formatFileSize = useCallback((bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }, []);

    const handlePreview = useCallback(async (doc) => {
        try {
            const result = await onPreview(doc.idDocument);
            if (!result.success) {
                alert(result.error || "Impossible d'ouvrir le document");
            }
        } catch (error) {
            alert("Erreur lors de l'ouverture du document");
        }
    }, [onPreview]);

    if (!isOpen || !selectedBC) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-full max-w-2xl mx-4 bg-white rounded-lg shadow-xl">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            Documents - {selectedBC?.numero_bc}
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                            {selectedBC?.etp_name}
                        </p>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 text-gray-400 transition-colors hover:text-gray-600"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Upload Section */}
                <div className="p-6 border-b border-gray-200">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-sm font-medium text-gray-900">Ajouter un document</h3>
                                <p className="mt-1 text-xs text-gray-500">
                                    PDF, DOC, XLS, images - Max. 10MB
                                </p>
                            </div>
                            {!selectedFile && (
                                <button
                                    onClick={handleTriggerFileInput}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors bg-purple-600 rounded-lg hover:bg-purple-700"
                                >
                                    <Upload className="w-4 h-4" />
                                    Sélectionner un fichier
                                </button>
                            )}
                        </div>

                        {/* File preview before upload */}
                        {selectedFile && (
                            <div className="p-4 border-2 border-purple-200 border-dashed rounded-lg bg-purple-50">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        {getFileIcon(selectedFile.name)}
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">
                                                {selectedFile.name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {formatFileSize(selectedFile.size)}
                                            </p>
                                        </div>
                                    </div>
                                    {uploadStatus !== 'uploading' && (
                                        <button
                                            onClick={handleCancelUpload}
                                            className="p-1 text-gray-400 hover:text-gray-600"
                                        >
                                            <XCircle className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>

                                {/* Progress bar */}
                                {uploadStatus === 'uploading' && (
                                    <div className="space-y-2">
                                        <div className="w-full h-2 overflow-hidden bg-purple-100 rounded-full">
                                            <div
                                                className="h-full transition-all duration-300 ease-in-out bg-purple-600"
                                                style={{ width: `${uploadProgress}%` }}
                                            />
                                        </div>
                                        <p className="text-xs text-center text-purple-600">
                                            Upload en cours... {uploadProgress}%
                                        </p>
                                    </div>
                                )}

                                {/* Success message */}
                                {uploadStatus === 'success' && (
                                    <div className="flex items-center gap-2 text-sm text-green-600">
                                        <CheckCircle className="w-4 h-4" />
                                        Fichier uploadé avec succès !
                                    </div>
                                )}

                                {/* Error message */}
                                {uploadStatus === 'error' && (
                                    <div className="flex items-center gap-2 text-sm text-red-600">
                                        <AlertCircle className="w-4 h-4" />
                                        {uploadError || "Erreur lors de l'upload"}
                                    </div>
                                )}

                                {/* Action buttons */}
                                {uploadStatus === 'idle' && (
                                    <div className="flex justify-end gap-2 mt-3">
                                        <button
                                            onClick={handleCancelUpload}
                                            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                        >
                                            Annuler
                                        </button>
                                        <button
                                            onClick={handleUpload}
                                            className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
                                        >
                                            Confirmer l'upload
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            onChange={handleFileSelect}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                        />
                    </div>
                </div>

                {/* Documents List */}
                <div className="p-6 overflow-y-auto max-h-96">
                    {documents.length === 0 ? (
                        <div className="py-8 text-center">
                            <File className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p className="text-sm text-gray-500">Aucun document uploadé</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {documents.map((doc) => (
                                <div
                                    key={doc.idDocument}
                                    className="flex items-center justify-between p-3 transition-colors border border-gray-200 rounded-lg hover:bg-gray-50"
                                >
                                    <div className="flex items-center flex-1 min-w-0 gap-3">
                                        {getFileIcon(doc.file_name)}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                {doc.file_name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {doc.file_size && formatFileSize(doc.file_size)}
                                                {doc.created_at && ` • ${dayjs(doc.created_at).format('DD/MM/YYYY HH:mm')}`}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handlePreview(doc)}
                                            className="p-2 text-gray-400 transition-colors hover:text-blue-600"
                                            title="Aperçu"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => onDownload(doc.idDocument)}
                                            className="p-2 text-gray-400 transition-colors hover:text-blue-600"
                                            title="Télécharger"
                                        >
                                            <Download className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteClick(doc.idDocument, doc.file_name)}
                                            className="p-2 text-gray-400 transition-colors hover:text-red-600"
                                            title="Supprimer"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t border-gray-200">
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 transition-colors bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        Fermer
                    </button>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteConfirmation.isOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
                    <div className="w-full max-w-md mx-4 bg-white rounded-lg shadow-xl">
                        <div className="p-6">
                            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                                <AlertTriangle className="w-6 h-6 text-red-600" />
                            </div>

                            <h3 className="mb-2 text-lg font-semibold text-center text-gray-900">
                                Confirmer la suppression
                            </h3>

                            <p className="mb-6 text-sm text-center text-gray-500">
                                Êtes-vous sûr de vouloir supprimer le document :<br />
                                <span className="font-medium text-gray-700">
                                    {deleteConfirmation.fileName}
                                </span>
                            </p>

                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={handleCancelDelete}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 transition-colors bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={handleConfirmDelete}
                                    className="px-4 py-2 text-sm font-medium text-white transition-colors bg-red-600 rounded-lg hover:bg-red-700"
                                >
                                    Supprimer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

DocumentsModal.displayName = 'DocumentsModal';
export default DocumentsModal;